import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { DateUtils } from '@pbt/pbt-ui-components'

import {
  type AgentChatMessage,
  type AgentChatSession,
  getSession,
  startAgentChatSession,
} from '~/api/agentChat'
import {
  createSSEConnection,
  type SSEMessage,
  SSEMessageUtils,
} from '~/api/sseCore'
import { getCurrentUserId } from '~/store/reducers/auth'

export interface UseAgentChatOptions {
  onSessionCreated?: (session: AgentChatSession) => void
  onSessionUpdate?: (session: AgentChatSession) => void
  userId?: string
}

export interface UseAgentChatReturn {
  clearMessages: () => void
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
  disconnect: () => void
  error: string | null
  isResponding: boolean
  loadSession: (sessionId: string) => Promise<void>
  loading: boolean
  messages: AgentChatMessage[]
  sendMessage: (content: string) => Promise<void>
  session: AgentChatSession | null
  startSession: () => Promise<void>
}

/**
 * Custom hook that manages agent chat functionality including:
 * - Session management
 * - Message sending and receiving via SSE
 * - Integration with Redux state
 * - Error handling
 */
export const useAgentChatAPI = (
  options: UseAgentChatOptions = {},
): UseAgentChatReturn => {
  const { userId, onSessionUpdate, onSessionCreated } = options

  const currentUserId = useSelector(getCurrentUserId)

  const [messages, setMessages] = useState<AgentChatMessage[]>([])
  const [session, setSession] = useState<AgentChatSession | null>(null)
  const [loading, setLoading] = useState(false)
  const [isResponding, setIsResponding] = useState(false)
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected')
  const [error, setError] = useState<string | null>(null)

  const sessionIdRef = useRef<string | null>(null)
  const streamRef = useRef<ReadableStream<Uint8Array> | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const effectiveUserId = userId || currentUserId || 'user'

  const transformSessionEvents = useCallback(
    (events: any[]): AgentChatMessage[] =>
      events
        .filter((event: any) => {
          // Filter out timestamp-only events or events without content
          if (!event.content) return false
          if (typeof event.content === 'object' && !event.content.parts)
            return false
          return true
        })
        .map((event: any) => {
          // Extract content from different possible formats
          let { content } = event
          let { sender } = event

          if (typeof content === 'object' && content?.parts?.[0]?.text) {
            content = content.parts[0].text
          } else if (typeof content !== 'string') {
            return undefined
          }

          // Determine sender from role if available
          if (typeof event.content === 'object' && event.content?.role) {
            sender = event.content.role === 'user' ? 'user' : 'agent'
          }

          return {
            ...event,
            timestamp: DateUtils.formatTime(
              moment(event.timestamp * 1000).toISOString(),
            ),
            content,
            sender: sender || event.sender || 'agent',
          }
        })
        .filter((message: any) => message !== undefined),
    [],
  )

  const startSession = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const newSession = await startAgentChatSession(effectiveUserId)
      sessionIdRef.current = newSession.id
      setSession(newSession)

      const transformedMessages = transformSessionEvents(
        newSession.events || [],
      )
      setMessages(transformedMessages)

      onSessionCreated?.(newSession)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to start session:', err)
      setError('Failed to start chat session')
      throw err
    } finally {
      setLoading(false)
    }
  }, [effectiveUserId, transformSessionEvents, onSessionCreated])

  const loadSession = useCallback(
    async (sessionId: string) => {
      try {
        setLoading(true)
        setError(null)

        const loadedSession = await getSession(effectiveUserId, sessionId)
        sessionIdRef.current = loadedSession.id
        setSession(loadedSession)

        const transformedMessages = transformSessionEvents(
          loadedSession.events || [],
        )
        setMessages(transformedMessages)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session:', err)
        setError('Failed to load session')
        setLoading(false)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [effectiveUserId, transformSessionEvents],
  )

  const sendMessage = useCallback(
    async (content: string) => {
      // Create a new session if none exists
      if (!sessionIdRef.current) {
        await startSession()
      }

      try {
        setError(null)
        setIsResponding(true)
        setConnectionState('connecting')

        // Optimistically add user message to UI
        const userMessage: AgentChatMessage = {
          id: crypto.randomUUID(),
          content,
          sender: 'user',
          timestamp: DateUtils.formatTime(moment().toISOString()),
          type: 'text',
        }

        // Add a loading message for the agent response
        const loadingMessage: AgentChatMessage = {
          id: crypto.randomUUID(),
          content: '',
          sender: 'agent',
          timestamp: '',
          type: 'text',
          isLoading: true,
        }

        setMessages((prev) => [...prev, userMessage, loadingMessage])

        // Send message via SSE
        const messagePayload = {
          appName: 'agents',
          newMessage: {
            role: 'user' as const,
            parts: [{ text: content }],
          },
          sessionId: sessionIdRef.current,
          streaming: true,
          userId: effectiveUserId,
        }

        const abortController = new AbortController()
        abortControllerRef.current = abortController

        const connectionResult = await createSSEConnection(
          {
            url: '/ai-agents/run_sse',
            method: 'POST',
            data: messagePayload,
            signal: abortController.signal,
          },
          {
            onOpen: () => {
              setConnectionState('connected')
            },
            onMessage: (data: SSEMessage) => {
              if (SSEMessageUtils.hasTextContent(data)) {
                const textContent = SSEMessageUtils.extractTextContent(data)

                setMessages((prev) => {
                  const loadingMessageIndex = prev.findIndex(
                    (msg) => msg.isLoading,
                  )

                  // Find the last non-loading agent message (iterate backwards)
                  let lastAgentMessageIndex = -1
                  for (let i = prev.length - 1; i >= 0; i--) {
                    if (prev[i].sender === 'agent' && !prev[i].isLoading) {
                      lastAgentMessageIndex = i
                      break
                    }
                  }
                  const lastMessage =
                    lastAgentMessageIndex >= 0
                      ? prev[lastAgentMessageIndex]
                      : null

                  // Update the last message ONLY if:
                  // 1. It exists and is an agent message
                  // 2. It's explicitly incomplete (isComplete === false, not undefined)
                  // This prevents overwriting historical messages from previous conversations
                  const shouldUpdateLastMessage =
                    lastMessage && lastMessage.isComplete === false

                  if (shouldUpdateLastMessage) {
                    // For partial=true: append. For partial=false: replace
                    const updatedMessage: AgentChatMessage = {
                      id: data.id || lastMessage.id,
                      content: data.partial
                        ? lastMessage.content + textContent
                        : textContent,
                      sender: 'agent',
                      timestamp: DateUtils.formatTime(
                        SSEMessageUtils.getTimestamp(data),
                      ),
                      type: 'text',
                      isLoading: false,
                      isComplete: !data.partial,
                      agent: data.agent || lastMessage.agent,
                      processedEvents:
                        data.processedEvents || lastMessage.processedEvents,
                      finalReportWithCitations:
                        data.finalReportWithCitations ||
                        lastMessage.finalReportWithCitations,
                    }

                    return [
                      ...prev.slice(0, lastAgentMessageIndex),
                      updatedMessage,
                      ...prev.slice(lastAgentMessageIndex + 1),
                    ]
                  }

                  // Create new message and insert before loading message
                  const agentMessage: AgentChatMessage = {
                    id: data.id || crypto.randomUUID(),
                    content: textContent,
                    sender: 'agent',
                    timestamp: DateUtils.formatTime(
                      SSEMessageUtils.getTimestamp(data),
                    ),
                    type: 'text',
                    isLoading: false,
                    isComplete: !data.partial,
                    agent: data.agent,
                    processedEvents: data.processedEvents,
                    finalReportWithCitations: data.finalReportWithCitations,
                  }

                  // Insert before loading message if it exists
                  if (loadingMessageIndex >= 0) {
                    return [
                      ...prev.slice(0, loadingMessageIndex),
                      agentMessage,
                      ...prev.slice(loadingMessageIndex),
                    ]
                  }

                  return [...prev, agentMessage]
                })
              }

              if (SSEMessageUtils.hasStateUpdates(data)) {
                setSession((prev) => {
                  if (!prev) return null

                  const updatedSession = {
                    ...prev,
                    state: { ...prev.state, ...data.actions!.stateDelta },
                  }

                  onSessionUpdate?.(updatedSession)

                  return updatedSession
                })
              }
            },
            onError: (eventError: Event) => {
              // eslint-disable-next-line no-console
              console.error('SSE error:', eventError)
              setConnectionState('error')
              setError('Connection error occurred')
              setIsResponding(false)

              // Remove loading message on error
              setMessages((prev) => prev.filter((msg) => !msg.isLoading))
            },
            onClose: () => {
              setConnectionState('disconnected')
              setIsResponding(false)
              setMessages((prev) => prev.filter((msg) => !msg.isLoading))
            },
          },
          {
            maxRetries: 3,
            maxDuration: 30000, // 30 seconds
            baseDelay: 1000,
            maxDelay: 5000,
          },
        )

        streamRef.current = connectionResult.stream
        readerRef.current = connectionResult.reader
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', err)
        setConnectionState('error')
        setError('Failed to send message')
        setIsResponding(false)

        // Remove loading message on error
        setMessages((prev) => prev.filter((msg) => !msg.isLoading))
        throw err
      }
    },
    [effectiveUserId, startSession],
  )

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (readerRef.current) {
      readerRef.current.cancel().catch((readerError) => {
        // eslint-disable-next-line no-console
        console.warn('Error canceling SSE reader:', readerError)
      })
      readerRef.current = null
    }

    if (streamRef.current) {
      streamRef.current = null
    }

    setConnectionState('disconnected')
    setSession(null)
    sessionIdRef.current = null
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  useEffect(
    () => () => {
      disconnect()
    },
    [disconnect],
  )

  return {
    messages,
    session,
    loading,
    isResponding,
    error,
    connectionState,
    sendMessage,
    startSession,
    loadSession,
    disconnect,
    clearMessages,
  }
}
